import type { GridApi } from "@mui/x-data-grid-pro"
import type { RefObject } from "react"
import { useCallback, useEffect, useRef, useState } from "react"
import subscribe from "./subscribe"
import type { EntityChange, RealtimeHeader } from "./model"
import { Operation } from "./model"
import { useAblyAuthMutation } from "./baseApi"
import { useAuth } from "@/auth"
import type { UpdateableEntity } from "@/utils"
import { isAfter, useDebounce } from "@/utils"

type Args<TEntity extends UpdateableEntity> = {
  autoRefresh: boolean
  apiRef: RefObject<GridApi | null>
  isRelevant: (entity: TEntity) => boolean
}

export default <TEntity extends UpdateableEntity>({
  autoRefresh,
  apiRef,
  isRelevant,
}: Args<TEntity>) => {
  const { userId } = useAuth()
  const [ablyAuth] = useAblyAuthMutation()
  const realtimeUnbindRef = useRef(() => {})
  const isMountedRef = useRef(true)
  const [hasNew, setHasNew] = useState(false)
  const hasNewDebounced = useDebounce(hasNew, {
    delay: "long",
    instantOnFalsy: true,
  })

  const gridRefresh = useCallback(() => {
    const api = apiRef.current
    if (!api) {
      return
    }

    api.dataSource.cache.clear()
    // This should work but doesn't remove deleted rows
    //api.dataSource.fetchRows()

    // So doing the dirty instead:
    const filterItem = { field: "__dummy__", operator: "is" }
    api.upsertFilterItem(filterItem)
    api.deleteFilterItem(filterItem)

    // TODO: revisit
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setHasNew(false)
  }, [apiRef])

  const gridSubscribe = useCallback(
    async ({ channel, event }: RealtimeHeader) => {
      const unbind = await subscribe(channel, () => ablyAuth().unwrap(), {
        [event]: ({ operation, payload, actorId }: EntityChange<TEntity>) => {
          const api = apiRef.current
          if (!api) {
            return
          }

          // TODO: Flash/animate changing row
          switch (operation) {
            case Operation.Insert:
              if (actorId !== userId) {
                setHasNew(true)
              }

              break
            case Operation.Update: {
              if (!isRelevant(payload)) {
                break
              }

              const cached = api.getRow<TEntity>(payload.id)
              if (!cached) {
                break
              }

              if (!isAfter(payload.updatedAt, cached.updatedAt)) {
                break
              }

              api.updateRows([payload])

              break
            }
            case Operation.Delete:
              api.updateRows([{ id: payload, _action: "delete" }])

              break
          }
        },
      })
      realtimeUnbindRef.current()

      if (!isMountedRef.current) {
        unbind()

        return
      }

      realtimeUnbindRef.current = unbind
    },
    [ablyAuth, apiRef, userId, isRelevant],
  )

  useEffect(() => {
    if (autoRefresh && hasNewDebounced) {
      gridRefresh()
    }
  }, [autoRefresh, hasNewDebounced, gridRefresh])

  useEffect(
    () => () => {
      isMountedRef.current = false
      realtimeUnbindRef.current()
      realtimeUnbindRef.current = () => {}
    },
    [],
  )

  return { gridSubscribe, hasNewRow: hasNewDebounced, gridRefresh }
}
